import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFileExport, faFileExcel, faFilePdf, faSort, faSortUp, faSortDown, faUpload, faSave, faTimes, faTicketAlt } from '@fortawesome/free-solid-svg-icons';
import flightManagementService from '../../../services/flightManagementService';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import BoardingPassModal from './BoardingPassModal';

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/no_aircraft.png';

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  return `${baseUrl}/${imageUrl}`;
};

const FlightDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.user_type === 'Admin' || user?.user_type === 'SubAdmin';
  const isAirliner = user?.user_type === 'Airliner';
  
  const [loading, setLoading] = useState(true);
  const [flightData, setFlightData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    bookingId: true,
    bookingReference: true,
    passengerNames: true,
    aircraftName: true,
    registrationNumber: true,
    flightNumber: true,
    departureAirport: true,
    departureDateTime: true,
    arrivalAirport: true,
    arrivalDateTime: true,
    selectedSeat: true,
    amount: true,
    notes: true,
    action: true
  });

  // Notes state - stores notes for each booking by booking ID
  const [bookingNotes, setBookingNotes] = useState({});
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [tempNote, setTempNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Cancellation request modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  // Boarding pass modal state
  const [showBoardingPassModal, setShowBoardingPassModal] = useState(false);
  const [selectedBookingForPass, setSelectedBookingForPass] = useState(null);

  useEffect(() => {
    fetchFlightDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);
      const response = await flightManagementService.getFlightDetails(id);

      if (response.success) {
        setFlightData(response.data);
        fetchBookings();
      }
    } catch (error) {
      // Error fetching flight details
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await flightManagementService.getFlightBookings(id);
      if (response.success) {
        const bookingsData = response.data || [];
        setBookings(bookingsData);
        
        // Load existing notes from bookings data
        const notesMap = {};
        bookingsData.forEach(booking => {
          if (booking.notes) {
            notesMap[booking.id] = booking.notes;
          }
        });
        setBookingNotes(notesMap);
      }
    } catch (error) {
      setBookings([]);
    }
  };

  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Notes handlers
  const handleEditNote = (bookingId, bookingToken, currentNote) => {
    setEditingNoteId(bookingId);
    setTempNote(currentNote || '');
  };

  const handleSaveNote = async (bookingId, bookingToken) => {
    try {
      setSavingNote(true);
      // Call API to save notes
      const response = await flightManagementService.updateBookingNotes(bookingToken, tempNote);
      
      if (response.success) {
        // Update local state
        setBookingNotes(prev => ({
          ...prev,
          [bookingId]: tempNote
        }));
        setEditingNoteId(null);
        setTempNote('');
        
        // Optional: Show success message
        console.log('Notes saved successfully');
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  // Upload handler
  const handleUpload = (bookingId) => {
    // Create a file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*'; // Accept all file types, you can restrict this
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Here you would typically upload the file to your server
        // For now, we'll just show an alert
        alert(`File "${file.name}" selected for booking ID: ${bookingId}\n\nImplement your upload logic here.`);
        // TODO: Implement actual file upload to your backend
        // Example:
        // const formData = new FormData();
        // formData.append('file', file);
        // formData.append('bookingId', bookingId);
        // await flightManagementService.uploadBookingDocument(formData);
      }
    };
    input.click();
  };

  // Delete/Cancel booking handler
  const handleDeleteBooking = (bookingId, bookingReference, bookingToken) => {
    setSelectedBooking({
      id: bookingId,
      reference: bookingReference,
      token: bookingToken || bookingReference,
    });
    setCancellationReason('');
    setShowCancelModal(true);
  };

  // Boarding pass handler
  const handleViewBoardingPass = (booking) => {
    setSelectedBookingForPass(booking);
    setShowBoardingPassModal(true);
  };

  // Airliner decline — sets RejectedByPJO; passenger chooses refund or change flight
  const handleSendCancellationRequest = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    try {
      setSendingRequest(true);
      
      // Call API to send cancellation request notification to admin
      const response = await flightManagementService.requestBookingCancellation(
        selectedBooking.token || selectedBooking.reference,
        cancellationReason
      );
      
      if (response.success) {
        toast.success(
          response.message ||
            `Booking declined. The passenger can choose a refund or change flight.`
        );
        setShowCancelModal(false);
        setSelectedBooking(null);
        setCancellationReason('');
        fetchBookings();
      } else {
        throw new Error(response.message || 'Failed to decline booking');
      }
    } catch (error) {
      console.error('Error declining booking:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to decline booking. Please try again.');
    } finally {
      setSendingRequest(false);
    }
  };

  // Direct cancel booking (for Admin)
  const handleAdminCancelBooking = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    try {
      setSendingRequest(true);
      
      // Call API to directly cancel the booking
      const response = await flightManagementService.cancelBookingDirect(
        selectedBooking.reference,
        cancellationReason
      );
      
      if (response.success) {
        toast.success(`Booking ${selectedBooking.reference} has been cancelled successfully`);
        setShowCancelModal(false);
        setSelectedBooking(null);
        setCancellationReason('');
        // Refresh bookings list
        fetchBookings();
      } else {
        throw new Error(response.message || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.message || 'Failed to cancel booking. Please try again.');
    } finally {
      setSendingRequest(false);
    }
  };

  const handleExport = (format) => {
    setShowExportDropdown(false);
    if (format === 'excel') {
      exportToExcel();
    } else if (format === 'pdf') {
      exportToPDF();
    }
  };

  const exportToExcel = () => {
    if (!confirmedBookings || confirmedBookings.length === 0) {
      alert('No confirmed bookings to export');
      return;
    }

    const data = confirmedBookings.map((booking, index) => ({
      '#': index + 1,
      'Booking ID': booking.id,
      'Booking Reference': booking.reference_number || 'N/A',
      'Passenger Name(s)': getPassengerNames(booking),
      'Aircraft Name': booking.aircraft_name || 'N/A',
      'Reg No.': booking.registration_number,
      'Flight No.': booking.flight_number,
      'Dep. Airport': booking.departure_airport,
      'Departure Date Time': formatDateTime(booking.departure_date_time),
      'Arr. Airport': booking.arrival_airport,
      'Arrival Date Time': formatDateTime(booking.arrival_date_time),
      'Selected Seat': booking.selected_seat || '-',
      'Amount': `$${parseFloat(booking.amount || 0).toFixed(2)}`
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bookings');
    XLSX.writeFile(workbook, `Flight_Bookings_${flightData?.flight_number || 'Export'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToPDF = () => {
    if (!confirmedBookings || confirmedBookings.length === 0) {
      alert('No confirmed bookings to export');
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape

    // Add header
    doc.setFontSize(18);
    doc.setTextColor(51, 51, 51);
    doc.text('Flight Bookings Report', 14, 15);

    doc.setFontSize(10);
    doc.setTextColor(102, 102, 102);
    doc.text(`Flight No: ${flightData?.flight_number || 'N/A'}`, 14, 25);
    doc.text(`Registration: ${flightData?.registration_number || 'N/A'}`, 14, 30);
    doc.text(`Route: ${flightData?.dep_airport || 'N/A'} - ${flightData?.arr_airport || 'N/A'}`, 14, 35);
    doc.text(`Departure Time: ${formatDateTime(flightData?.departure_date_time)}`, 14, 40);

    const tableColumn = [
      '#', 'Booking ID', 'Reference', 'Passenger(s)', 'Aircraft', 'Reg No.', 'Flight No.',
      'Dep.', 'Departure Date Time', 'Arr.', 'Arrival Date Time',
      'Seat', 'Amount'
    ];

    const tableRows = confirmedBookings.map((booking, index) => [
      index + 1,
      booking.id,
      booking.reference_number || 'N/A',
      getPassengerNames(booking),
      booking.aircraft_name || 'N/A',
      booking.registration_number,
      booking.flight_number,
      booking.departure_airport,
      formatDateTime(booking.departure_date_time),
      booking.arrival_airport,
      formatDateTime(booking.arrival_date_time),
      booking.selected_seat || '-',
      `$${parseFloat(booking.amount || 0).toFixed(2)}`
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 45,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [255, 210, 0], textColor: [0, 0, 0], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [250, 250, 250] }
    });

    doc.save(`Flight_Bookings_${flightData?.flight_number || 'Export'}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `$${parseFloat(price).toFixed(0)}`;
  };

  // Helper to get passenger names string
  const getPassengerNames = (booking) => {
    if (booking.passengers && booking.passengers.length > 0) {
      return booking.passengers.map(p => `${p.first_name} ${p.last_name}`).join(', ');
    }
    return 'N/A';
  };

  // Sorting handlers
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FontAwesomeIcon icon={faSort} className="ml-2 text-gray-400 opacity-50 text-[10px]" />;
    }
    return (
      <FontAwesomeIcon
        icon={sortConfig.direction === 'asc' ? faSortUp : faSortDown}
        className="ml-2 text-black text-[10px]"
      />
    );
  };

  // Filter, sort and paginate bookings
  const filteredBookings = useMemo(() => {
    return bookings.filter(booking => {
      const searchLower = searchTerm.toLowerCase();
      const pNames = getPassengerNames(booking).toLowerCase();
      return (
        booking.id?.toString().toLowerCase().includes(searchLower) ||
        booking.reference_number?.toString().toLowerCase().includes(searchLower) ||
        booking.registration_number?.toString().toLowerCase().includes(searchLower) ||
        booking.flight_number?.toString().toLowerCase().includes(searchLower) ||
        booking.departure_airport?.toString().toLowerCase().includes(searchLower) ||
        booking.arrival_airport?.toString().toLowerCase().includes(searchLower) ||
        booking.booking_status?.toLowerCase().includes(searchLower) ||
        pNames.includes(searchLower)
      );
    });
  }, [bookings, searchTerm]);

  const confirmedBookings = useMemo(() => {
    let result = filteredBookings.filter(b => b.booking_status === 'Confirmed');

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'bookingId': aValue = a.id; bValue = b.id; break;
          case 'bookingReference': aValue = a.reference_number || ''; bValue = b.reference_number || ''; break;
          case 'passengerNames': aValue = getPassengerNames(a); bValue = getPassengerNames(b); break;
          case 'aircraftName': aValue = a.aircraft_name || ''; bValue = b.aircraft_name || ''; break;
          case 'registrationNumber': aValue = a.registration_number || ''; bValue = b.registration_number || ''; break;
          case 'flightNumber': aValue = a.flight_number || ''; bValue = b.flight_number || ''; break;
          case 'departureAirport': aValue = a.departure_airport || ''; bValue = b.departure_airport || ''; break;
          case 'departureDateTime':
            aValue = a.departure_date_time ? new Date(a.departure_date_time).getTime() : 0;
            bValue = b.departure_date_time ? new Date(b.departure_date_time).getTime() : 0;
            break;
          case 'arrivalAirport': aValue = a.arrival_airport || ''; bValue = b.arrival_airport || ''; break;
          case 'arrivalDateTime':
            aValue = a.arrival_date_time ? new Date(a.arrival_date_time).getTime() : 0;
            bValue = b.arrival_date_time ? new Date(b.arrival_date_time).getTime() : 0;
            break;
          case 'selectedSeat': aValue = a.selected_seat || ''; bValue = b.selected_seat || ''; break;
          case 'amount': aValue = parseFloat(a.amount || 0); bValue = parseFloat(b.amount || 0); break;
          default: aValue = a[sortConfig.key]; bValue = b[sortConfig.key];
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [filteredBookings, sortConfig]);

  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    return confirmedBookings.slice(startIndex, startIndex + recordsPerPage);
  }, [confirmedBookings, currentPage, recordsPerPage]);

  const totalPages = Math.ceil(confirmedBookings.length / recordsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd200] mx-auto mb-4"></div>
          <p className="text-[#555]">Loading flight details...</p>
        </div>
      </div>
    );
  }

  if (!flightData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#333] mb-4">Flight Not Found</h2>
          <p className="text-[#555] mb-6">The flight you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/airliner/flights')}
            className="bg-gradient-to-br from-[#ffeb00] to-[#ffd200] text-black font-medium px-6 py-3 rounded-md hover:from-[#ffd200] hover:to-[#ffb300]"
          >
            Back to Flights
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 bg-jet-gray-100 min-h-screen w-full relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif]">
      {/* Header Section */}
      <div className="py-5 px-[30px] max-sm:px-4 flex justify-between items-center max-sm:flex-col max-sm:items-start gap-3 bg-transparent">
        <h1 className="m-0 text-[18px] font-bold mb-[7px] font-['Bahnschrift_Light'] text-[#333]">Flight Information</h1>
        <div className="flex gap-2 flex-wrap max-sm:w-full">
          {/* {
            (flightData.is_draft === 1 || flightData.is_draft === '1') && (
              <button
                onClick={() => navigate(`/airliner/edit-flight/${flightData.id}`)}
                className="text-black h-9 py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 hover:from-[#ffd200] hover:to-[#ffb300]"
              >
                <FontAwesomeIcon icon={faEdit} />
                Edit
              </button>
            )
          } */}
          <button
            onClick={() => navigate(-1)}
            className="text-black h-9 py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 hover:from-[#ffd200] hover:to-[#ffb300]"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </button>

          {bookings.length > 0 && (
            <>
              {/* Export Dropdown */}
              <div className="relative inline-block">
                <button
                  onClick={() => {
                    setShowExportDropdown(!showExportDropdown);
                    setShowColumnDropdown(false);
                  }}
                  className="text-black h-9 py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 hover:from-[#ffd200] hover:to-[#ffb300]"
                >
                  <FontAwesomeIcon icon={faFileExport} />
                  Export
                </button>
                {showExportDropdown && (
                  <ul className="absolute right-0 mt-1 w-[150px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded shadow-lg z-10 list-none p-0 m-0">
                    <li>
                      <button
                        onClick={() => handleExport('excel')}
                        className="w-full text-left px-4 py-3 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] flex items-center gap-2 text-[14px] border-0 bg-transparent text-black cursor-pointer"
                      >
                        <FontAwesomeIcon icon={faFileExcel} />
                        Excel
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleExport('pdf')}
                        className="w-full text-left px-4 py-3 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] flex items-center gap-2 text-[14px] border-0 bg-transparent text-black cursor-pointer"
                      >
                        <FontAwesomeIcon icon={faFilePdf} />
                        PDF
                      </button>
                    </li>
                  </ul>
                )}
              </div>

              {/* Hide/Show Column Dropdown */}
              <div className="relative inline-block">
                <button
                  onClick={() => {
                    setShowColumnDropdown(!showColumnDropdown);
                    setShowExportDropdown(false);
                  }}
                  className="text-black h-9 py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 hover:from-[#ffd200] hover:to-[#ffb300]"
                >
                  Hide/Show Column
                </button>
                {showColumnDropdown && (
                  <ul className="absolute right-0 max-sm:left-0 max-sm:right-auto mt-1 w-[220px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded shadow-lg z-10 p-2 max-h-[300px] overflow-y-auto list-none m-0">
                    {Object.entries({
                      bookingId: 'Booking ID',
                      bookingReference: 'Booking Reference',
                      passengerNames: 'Passenger Name(s)',
                      aircraftName: 'Aircraft Name',
                      registrationNumber: 'Reg No.',
                      flightNumber: 'Flight No.',
                      departureAirport: 'Dep. Airport',
                      departureDateTime: 'Departure Date Time',
                      arrivalAirport: 'Arr. Airport',
                      arrivalDateTime: 'Arrival Date Time',
                      selectedSeat: 'Selected Seat',
                      amount: 'Amount',
                      notes: 'Notes',
                      action: 'Action'
                    }).map(([key, label]) => (
                      <li key={key}>
                        <label className="flex items-center gap-2 py-2 px-2 cursor-pointer hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] rounded">
                          <input
                            type="checkbox"
                            checked={visibleColumns[key]}
                            onChange={() => toggleColumn(key)}
                            className="cursor-pointer"
                          />
                          <span className="text-[13px] text-black">{label}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="py-0 px-[30px] max-sm:px-4 pb-[30px]">
        {/* Flight Information Box */}
        <section className="bg-white p-[20px] max-sm:p-4 mb-5 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.05)] mt-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-[20px] gap-y-0">
            {/* Aircraft Image - Left Side */}
            <div className="flex flex-col mb-4 md:mb-0 max-sm:items-center">
              <img
                src={
                  flightData.aircraft_images && flightData.aircraft_images.length > 0 && flightData.aircraft_images[0].presigned_url
                    ? getImageUrl(flightData.aircraft_images[0].presigned_url)
                    : "/no_aircraft.png"
                }
                alt="Aircraft"
                className="w-full rounded-[8px] max-w-[200px]"
                onError={(e) => { e.target.src = '/no_aircraft.png'; }}
              />
            </div>

            {/* Flight Information List - Right Side (3 columns) */}
            <div className="md:col-span-3">
              <div className="space-y-0">
                <div className="flex justify-between py-[10px] border-b border-t border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">Reg No.</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{flightData.registration_number || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">Flight Number</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{flightData.flight_number || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">Dep. Airport</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{flightData.dep_airport || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">ETD</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{formatDateTime(flightData.departure_date_time)}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">Arr. Airport</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{flightData.arr_airport || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">ETA</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{formatDateTime(flightData.arrival_date_time)}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">BEL Price</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{formatPrice(flightData.block_empty_price)}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">EL Price</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{formatPrice(flightData.block_empty_price > 0 ? flightData.block_empty_price : flightData.block_price)}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">Seat Price</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{formatPrice(flightData.seat_price)}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">Seat(s) (Total)</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{flightData.seat_total || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">Available Seats</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{flightData.seat_available ?? 'N/A'}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b border-[#f4f4f4]">
                  <span className="text-[14px] text-[#555]">Cancellation Fee ($)</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{flightData.cancellation_fee || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-[10px] border-b">
                  <span className="text-[14px] text-[#555]">Agency Fee (%)</span>
                  <span className="text-[14px] font-bold text-[#333] text-right">{flightData.commission_fee || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bookings Table */}
        <section className="bg-white p-[20px] max-sm:p-4 mb-5 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
          <h2 className="text-xl font-semibold m-0 mb-6 text-[#333] font-['Bahnschrift_Light']">
            Flight(s) Confirmed
          </h2>

          <div className="flex justify-between items-center mb-4 gap-4 max-md:flex-col max-md:items-stretch max-md:gap-3">
            <div className="flex items-center gap-2 text-[13px] text-gray-600">
              <span>Display</span>
              <select
                value={recordsPerPage}
                onChange={handleRecordsPerPageChange}
                className="border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:border-[#ffd200]"
              >
                {[10, 25, 50, 100].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>Records</span>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-gray-600 max-md:flex-col max-md:items-stretch max-md:w-full max-md:gap-2">
              <div className="flex items-center gap-2 mr-2 max-md:mr-0 max-md:w-full">
                <span className="flex-shrink-0">Search</span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="border border-gray-300 rounded px-3 py-1 bg-white focus:outline-none focus:border-[#ffd200] w-[200px] max-md:w-full max-md:flex-1"
                  placeholder="Search bookings..."
                />
              </div>

              {confirmedBookings.length > 0 && (
                <div className="flex items-center gap-0 border border-gray-300 rounded overflow-x-auto max-w-full h-[30px] max-md:w-full max-md:h-[34px]">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className={`px-3 py-0 text-[12px] border-r border-gray-300 h-full flex-shrink-0 max-md:flex-1 ${currentPage === 1 ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100 transition-colors'}`}
                  >
                    Previous
                  </button>

                  {[...Array(Math.max(1, totalPages))].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => handlePageChange(i + 1)}
                      className={`px-3 py-0 text-[12px] border-r border-gray-300 h-full last:border-r-0 flex-shrink-0 ${currentPage === i + 1 ? 'bg-[#ffd200] text-black font-semibold' : 'bg-white text-gray-700 hover:bg-gray-100 transition-colors'}`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className={`px-3 py-0 text-[12px] h-full flex-shrink-0 max-md:flex-1 ${currentPage === totalPages || totalPages === 0 ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100 transition-colors'}`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white mx-0 mt-0 mb-[15px] rounded overflow-x-auto shadow-[0_1px_3px_rgba(0,0,0,0.05)] border border-[#e0e0e0]">
            <table className="w-full border-collapse bg-white min-w-[1200px]">
              <thead className="bg-[#fafafa] border-b-2 border-[#e0e0e0]">
                <tr>
                  <th className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap w-[40px]">#</th>
                  {visibleColumns.bookingId && (
                    <th
                      onClick={() => handleSort('bookingId')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Booking ID {renderSortIcon('bookingId')}
                    </th>
                  )}
                  {visibleColumns.bookingReference && (
                    <th
                      onClick={() => handleSort('bookingReference')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Booking Reference {renderSortIcon('bookingReference')}
                    </th>
                  )}
                  {visibleColumns.passengerNames && (
                    <th
                      onClick={() => handleSort('passengerNames')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Passenger Name(s) {renderSortIcon('passengerNames')}
                    </th>
                  )}
                  {visibleColumns.aircraftName && (
                    <th
                      onClick={() => handleSort('aircraftName')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Aircraft Name {renderSortIcon('aircraftName')}
                    </th>
                  )}
                  {visibleColumns.registrationNumber && (
                    <th
                      onClick={() => handleSort('registrationNumber')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Reg No. {renderSortIcon('registrationNumber')}
                    </th>
                  )}
                  {visibleColumns.flightNumber && (
                    <th
                      onClick={() => handleSort('flightNumber')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Flight No. {renderSortIcon('flightNumber')}
                    </th>
                  )}
                  {visibleColumns.departureAirport && (
                    <th
                      onClick={() => handleSort('departureAirport')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Dep. Airport {renderSortIcon('departureAirport')}
                    </th>
                  )}
                  {visibleColumns.departureDateTime && (
                    <th
                      onClick={() => handleSort('departureDateTime')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Departure Date Time {renderSortIcon('departureDateTime')}
                    </th>
                  )}
                  {visibleColumns.arrivalAirport && (
                    <th
                      onClick={() => handleSort('arrivalAirport')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Arr. Airport {renderSortIcon('arrivalAirport')}
                    </th>
                  )}
                  {visibleColumns.arrivalDateTime && (
                    <th
                      onClick={() => handleSort('arrivalDateTime')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Arrival Date Time {renderSortIcon('arrivalDateTime')}
                    </th>
                  )}
                  {visibleColumns.selectedSeat && (
                    <th
                      onClick={() => handleSort('selectedSeat')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Selected Seat {renderSortIcon('selectedSeat')}
                    </th>
                  )}
                  {visibleColumns.amount && (
                    <th
                      onClick={() => handleSort('amount')}
                      className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      Amount {renderSortIcon('amount')}
                    </th>
                  )}
                  {visibleColumns.notes && (
                    <th className="py-2 px-3 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap min-w-[200px]">
                      Notes
                    </th>
                  )}
                  {visibleColumns.action && (
                    <th className="py-2 px-3 text-center font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-[#fafafa] align-middle whitespace-nowrap">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedBookings.length > 0 ? (
                  paginatedBookings.map((booking, index) => (
                    <tr key={booking.id} className="transition-colors duration-200 hover:bg-[#f9f9f9] border-b border-[#f4f4f4]">
                      <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                        {(currentPage - 1) * recordsPerPage + index + 1}
                      </td>
                      {visibleColumns.bookingId && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">{booking.id}</td>
                      )}
                      {visibleColumns.bookingReference && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap font-medium uppercase">{booking.reference_number || 'N/A'}</td>
                      )}
                      {visibleColumns.passengerNames && (
                        <td className="py-2.5 px-3 text-left text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle min-w-[150px]">
                          {getPassengerNames(booking)}
                        </td>
                      )}
                      {visibleColumns.aircraftName && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                          {booking.aircraft_name || 'N/A'}
                        </td>
                      )}
                      {visibleColumns.registrationNumber && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap uppercase">
                          {booking.registration_number}
                        </td>
                      )}
                      {visibleColumns.flightNumber && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap uppercase">
                          {booking.flight_number}
                        </td>
                      )}
                      {visibleColumns.departureAirport && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                          {booking.departure_airport}
                        </td>
                      )}
                      {visibleColumns.departureDateTime && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                          {formatDateTime(booking.departure_date_time)}
                        </td>
                      )}
                      {visibleColumns.arrivalAirport && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                          {booking.arrival_airport}
                        </td>
                      )}
                      {visibleColumns.arrivalDateTime && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                          {formatDateTime(booking.arrival_date_time)}
                        </td>
                      )}
                      {visibleColumns.selectedSeat && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                          {booking.selected_seat || '-'}
                        </td>
                      )}
                      {visibleColumns.amount && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                          ${parseFloat(booking.amount || 0).toFixed(2)}
                        </td>
                      )}
                      {visibleColumns.notes && (
                        <td className="py-2.5 px-3 text-left text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle min-w-[200px]">
                          {editingNoteId === booking.id ? (
                            <div className="flex items-center gap-2">
                              <textarea
                                value={tempNote}
                                onChange={(e) => setTempNote(e.target.value)}
                                className="w-48 h-8 px-2 py-1 text-sm border border-gray-300 rounded resize-y focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
                                placeholder="Add note..."
                                autoFocus
                              />
                              <button
                                onClick={() => handleSaveNote(booking.id, booking.reference_number)}
                                className="w-8 h-8 rounded bg-[#03538f] text-white flex items-center justify-center hover:bg-[#2d4373] transition-colors disabled:opacity-50"
                                title="Save"
                                disabled={savingNote}
                              >
                                <FontAwesomeIcon icon={faSave} className="text-[14px]" />
                              </button>
                            </div>
                          ) : (
                            <div
                              onClick={() => handleEditNote(booking.id, booking.reference_number, bookingNotes[booking.id])}
                              className="cursor-pointer hover:bg-gray-50 p-2 rounded min-h-[32px] border border-transparent hover:border-gray-200 transition-colors"
                              title="Click to edit note"
                            >
                              {bookingNotes[booking.id] || <span className="text-gray-400 italic">Click to add note...</span>}
                            </div>
                          )}
                        </td>
                      )}
                      {visibleColumns.action && (
                        <td className="py-2.5 px-3 text-center text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white align-middle whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2">
                            {/* Boarding Pass Button */}
                            <button
                              onClick={() => handleViewBoardingPass(booking)}
                              className="bg-gradient-to-br from-blue-400 to-blue-500 text-white px-3 py-2 rounded text-[14px] font-medium hover:from-blue-500 hover:to-blue-600 transition-all duration-200 inline-flex items-center justify-center"
                              title="View boarding pass"
                            >
                              <FontAwesomeIcon icon={faTicketAlt} />
                            </button>
                            {/* Upload button - only visible for Airliner */}
                            {isAirliner && (
                              <button
                                onClick={() => handleUpload(booking.id)}
                                className="bg-gradient-to-br from-[#ffeb00] to-[#ffd200] text-black px-3 py-2 rounded text-[14px] font-medium hover:from-[#ffd200] hover:to-[#ffb300] transition-all duration-200 inline-flex items-center justify-center"
                                title="Upload document"
                              >
                                <FontAwesomeIcon icon={faUpload} />
                              </button>
                            )}
                            {/* Cancel button - visible for both Admin and Airliner */}
                            <button
                              onClick={() =>
                                handleDeleteBooking(
                                  booking.id,
                                  booking.reference_number,
                                  booking.booking_token
                                )
                              }
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-[14px] transition-all duration-200 inline-flex items-center justify-center"
                              title="Cancel booking"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={Object.values(visibleColumns).filter(Boolean).length + 1}
                      className="text-center p-8 text-[#aaa] text-[14px] font-light italic"
                    >
                      No confirmed bookings found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-between items-center py-4 gap-2">
            <div className="text-[13px] text-gray-500 whitespace-nowrap">
              Showing {confirmedBookings.length > 0 ? (currentPage - 1) * recordsPerPage + 1 : 0} to {Math.min(currentPage * recordsPerPage, confirmedBookings.length)} of {confirmedBookings.length} total entries
            </div>
          </div>
        </section>
      </div>

      {/* Cancellation Request Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-[#333] mb-2">
                {isAdmin ? 'Cancel Booking' : 'Decline Booking'}
              </h3>
              <div className="border-b-2 border-[#ffd200] w-16 mb-4"></div>
            </div>
            
            <div className="mb-6">
              {isAdmin ? (
                <p className="text-[#555] text-[15px] leading-relaxed mb-4">
                  Are you sure you want to cancel this confirmed booking? This action cannot be undone.
                </p>
              ) : (
                <p className="text-[#555] text-[15px] leading-relaxed mb-4">
                  The passenger will be notified and can choose a full refund or change flight.
                  No admin refund request is created.
                </p>
              )}
              
              {selectedBooking && (
                <div className="mt-4 p-3 bg-gray-50 rounded border border-gray-200 mb-4">
                  <p className="text-[13px] text-[#666]">
                    <span className="font-semibold">Booking Reference:</span> {selectedBooking.reference}
                  </p>
                </div>
              )}
              
              <div className="mt-4">
                <label className="block text-[14px] font-semibold text-[#333] mb-2">
                  Reason for declining <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  className="w-full px-3 py-2 text-[14px] border border-gray-300 rounded resize-y focus:outline-none focus:ring-2 focus:ring-[#ffd200] min-h-[100px]"
                  placeholder="Please provide a reason for declining this booking..."
                  disabled={sendingRequest}
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedBooking(null);
                  setCancellationReason('');
                }}
                className="px-4 py-2 text-[#555] bg-gray-200 rounded hover:bg-gray-300 transition-colors font-medium"
                disabled={sendingRequest}
              >
                Close
              </button>
              <button
                onClick={isAdmin ? handleAdminCancelBooking : handleSendCancellationRequest}
                className="px-6 py-2 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] text-black rounded hover:from-[#ffd200] hover:to-[#ffb300] transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sendingRequest}
              >
                {sendingRequest ? 'Processing...' : (isAdmin ? 'Confirm Cancellation' : 'Confirm Decline')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Boarding Pass Modal */}
      {showBoardingPassModal && selectedBookingForPass && (
        <BoardingPassModal
          booking={selectedBookingForPass}
          flightData={flightData}
          onClose={() => {
            setShowBoardingPassModal(false);
            setSelectedBookingForPass(null);
          }}
        />
      )}
    </div>
  );
};


export default FlightDetailsPage;
